import { translate } from '@/translation/translate.ts';
import { FC, KeyboardEvent } from 'react';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import { ICONS } from '@/Icons.tsx';
import { ConfigTranslatedText, PeselSlide } from '@/eventConfig/types';

export type PeselCheckStatus = 'init' | 'checking' | 'ok' | 'not-ok';

interface PeselCheckButtonProps {
  slide: PeselSlide;
  disabled: boolean;
  status: PeselCheckStatus;
  onCheck: () => void;
}

interface SpinnerProps {
  slide: PeselSlide;
}

const Spinner: FC<SpinnerProps> = ({ slide }) => {
  return (
    <div className="pesel-check-status" role="status" aria-live="polite" aria-busy={true}>
      <i className={ICONS.spinner} aria-hidden="true" />
      <span>{translate(slide.peselResultTexts?.checking)}</span>
    </div>
  );
};

const Success: FC<SpinnerProps> = ({ slide }) => {
  return (
    <div className="pesel-check-status" role="status" aria-live="polite" aria-busy={false}>
      <i className={ICONS.success} aria-hidden="true" />
      <span>{translate(slide.peselResultTexts?.ok)}</span>
    </div>
  );
};

const Failed: FC<SpinnerProps> = ({ slide }) => {
  return (
    <div className="pesel-check-status" role="status" aria-live="polite" aria-busy={false}>
      <i className={ICONS.failed} aria-hidden="true" />
      <span>{translate(slide.peselResultTexts?.alreadyRegistered)}</span>
    </div>
  );
};

const renderButtonContent = (status: PeselCheckStatus, label: ConfigTranslatedText | undefined, slide: PeselSlide) => {
  {
    switch (status) {
      case 'checking':
        return <Spinner slide={slide} />;
      case 'ok':
        return <Success slide={slide} />;
      case 'not-ok':
        return <Failed slide={slide} />;
      case 'init':
      default:
        return translate(label);
    }
  }
};

export const PeselCheckButton: FC<PeselCheckButtonProps> = ({ disabled, onCheck, status }) => {
  const { eventConfig } = useEventConfig();
  const s = eventConfig!.slides;

  const handleKeyActivate = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      onCheck();
    }
  };

  return (
    <button
      type="button"
      disabled={disabled}
      aria-disabled={disabled}
      aria-busy={status === 'checking'}
      className={'col-6 check-button'}
      onClick={onCheck}
      onKeyDown={handleKeyActivate}
    >
      {renderButtonContent(status, s.peselCheck.saveLabel, s.peselCheck)}
    </button>
  );
};
